import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import PostAddIcon from "@mui/icons-material/PostAdd";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { Theme } from "@mui/material/styles";
import {
  createMRTColumnHelper,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_Row,
  type MRT_TableInstance,
  type MRT_TableOptions,
} from "material-react-table";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import CondensedCell from "_shared/_views/tables/condensed-cell";
import RowActions from "_shared/_views/tables/row-actions";
import SearchMatch from "_shared/_views/tables/search-match";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import {
  searchFieldSpacing,
  searchFieldWidth,
  searchTextFieldProps,
  ToolbarButtons,
  topToolbarFitsThePage,
} from "_shared/_views/tables/table-toolbar";
import TruncatedCell from "_shared/_views/tables/truncated-cell";
import { DateCell } from "_shared/_views/tables/date-cells";
import { NoValueDash } from "_shared/_views/tables/no-value-dash";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import "permissions/__extensions/users/user-extensions";
import useSessionUser from "permissions/session/use-session-user";
import { useState, type ReactNode } from "react";
import { useSession } from "users/__session/session-context";
import {
  useDeleteProposalTemplateMutation,
  useGetProposalTemplatesQuery,
} from "work/proposal-templates/_api/proposal-template-api";
import useStartDraftFromProposalTemplate from "work/proposal-templates/_hooks/use-start-draft-from-proposal-template";
import { deleteTemplateNamed } from "work/proposal-templates/delete-proposal-template";
import ProposalTemplate from "work/proposal-templates/proposal-template";
import {
  ListPageHeading,
  ListPageMobileSearch,
  ListPageMobileToolbar,
  ListPageToolbarButtons,
} from "_shared/_views/list-pages/list-page-heading";

export const START_DRAFT_FROM_TEMPLATE_LABEL = "Create Proposal from Template";
export const DELETE_TEMPLATE_LABEL = "Delete Template";
export const PROPOSAL_TEMPLATES_SUBJECT = "proposal templates";
/** Marks the template's own name in its two-line Name cell, apart from the proposal name under it. */
export const TEMPLATE_NAME_TEST_ID = "proposal-template-name";
export const NO_PROPOSAL_TEMPLATES_DESCRIPTION =
  "Open a proposal and choose Save as Proposal Template to make one.";

const columnHelper = createMRTColumnHelper<ProposalTemplate>();

/**
 * The Proposals page's Templates tab (#6261): the reader's saved proposal templates, for the side
 * they are working on.
 *
 * The API answers the reader's whole set, unpaged, so the table searches, sorts and filters the rows
 * it holds, as [template panels](docs/software-design/template-panels.md) asks of a list that has
 * every row. Ordering a page of rows and calling it the order of all of them is the lie that rule
 * prevents, and there is no page here to tell it about.
 *
 * Templates from the other side are left out, as the Create from Template dialog leaves them out: a
 * draft can only be started from one on the side it was saved from.
 */
export default function ProposalTemplatesTable(
  props: Readonly<{
    /** The Proposals page's tab strip, drawn in this table's heading as every other tab draws it. */
    tabs?: ReactNode;
  }>,
) {
  const { tabs } = props;
  const session = useSession();
  const sessionUser = useSessionUser();
  const confirm = useConfirmDialog();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const templatesQuery = useGetProposalTemplatesQuery({});
  const [deleteTemplate] = useDeleteProposalTemplateMutation();
  const draftFromTemplate = useStartDraftFromProposalTemplate();
  const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(null);

  const templates = usableTemplates(templatesQuery.data, session);
  const canCreateProposal = sessionUser?.canCreateProposals() ?? false;

  async function handleDelete(template: ProposalTemplate) {
    const response = await confirm({
      title: "Delete proposal template?",
      message: `Delete the proposal template ${template.name}? Drafts already started from it do not change.`,
      okButtonText: DELETE_TEMPLATE_LABEL,
      cancelButtonText: "Keep Template",
    });
    if (response === ConfirmResponse.Cancel) return;
    setDeletingTemplateId(template.id.value);
    await deleteTemplateNamed(template, (templateId) => deleteTemplate({ templateId }).unwrap());
    setDeletingTemplateId(null);
  }

  // Each column says what it holds, so the shared fit sizes it from its heading and its rows, and a
  // name or description too long for its share ends in an ellipsis the table's tooltip reveals (#6721).
  // The proposal name reads under the template's name, so the list keeps to six columns; Show/hide
  // still offers it as a column of its own. The search reads the three text columns, so they draw
  // `renderedCellValue`, which carries the table's mark of a match (#6590), and the line under the
  // name marks it as well.
  const columns = [
    columnHelper.accessor((template) => template.name, {
      id: "name",
      header: "Name",
      meta: { kind: "name" },
      Cell: ({ renderedCellValue, row, table }) => {
        const proposalName = row.original.proposalName;
        const isProposalNameUnderName = !table.getColumn("proposalName").getIsVisible();
        return (
          <CondensedCell
            title={<span data-testid={TEMPLATE_NAME_TEST_ID}>{renderedCellValue}</span>}
            secondary={
              isProposalNameUnderName && proposalName ? <SearchMatch table={table} text={proposalName} /> : undefined
            }
          />
        );
      },
    }),
    columnHelper.accessor((template) => template.proposalName, {
      id: "proposalName",
      header: "Proposal name",
      meta: { kind: "text" },
      Cell: ({ renderedCellValue }) => (renderedCellValue ? <TruncatedCell>{renderedCellValue}</TruncatedCell> : <NoValueDash />),
    }),
    columnHelper.accessor((template) => template.description, {
      id: "description",
      header: "Description",
      meta: { kind: "text" },
      enableSorting: false,
      Cell: ({ renderedCellValue }) => (renderedCellValue ? <TruncatedCell>{renderedCellValue}</TruncatedCell> : <NoValueDash />),
    }),
    columnHelper.accessor((template) => teamSize(template), {
      id: "team",
      header: "Team",
      meta: { kind: "number" },
      enableGlobalFilter: false,
      enableColumnFilter: false,
      Cell: ({ cell }) => describeTeamSize(cell.getValue()),
    }),
    columnHelper.accessor((template) => template.discount.value, {
      id: "discount",
      header: "Discount",
      meta: { kind: "number" },
      enableGlobalFilter: false,
      enableColumnFilter: false,
      Cell: ({ row }) => row.original.discount.toString(),
    }),
    columnHelper.accessor((template) => String(template.negotiable), {
      id: "negotiable",
      header: "Negotiable",
      meta: { kind: "status" },
      enableGlobalFilter: false,
      enableSorting: false,
      filterVariant: "select",
      filterSelectOptions: [
        { value: "true", label: "Negotiable" },
        { value: "false", label: "Non-negotiable" },
      ],
      filterFn: "equals",
      Cell: ({ row }) =>
        row.original.negotiable ? <CheckIcon aria-label="Negotiable" /> : <CloseIcon aria-label="Non-negotiable" />,
    }),
    columnHelper.accessor((template) => template.created, {
      id: "created",
      header: "Created",
      meta: { kind: "date" },
      enableGlobalFilter: false,
      sortingFn: "datetime",
      filterVariant: "date-range",
      filterFn: createdWithinFilter,
      Cell: ({ row }) => <DateCell value={row.original.created} />,
    }),
  ];

  function renderToolbarButtons(table: MRT_TableInstance<ProposalTemplate>) {
    return (
      <ToolbarButtons>
        <TableToolsMenu table={table} />
      </ToolbarButtons>
    );
  }

  const options: MRT_TableOptions<ProposalTemplate> = {
    columns,
    data: templates,
    enableColumnFilters: true,
    enableDensityToggle: false,
    enableRowSelection: false,
    enableRowActions: true,
    enableColumnPinning: true,
    enableStickyHeader: true,
    positionActionsColumn: "last",
    positionGlobalFilter: "none",
    globalFilterFn: "contains",
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
      columnVisibility: { proposalName: false },
      showGlobalFilter: true,
      sorting: [{ id: "created", desc: true }],
    },
    // While the first load is in flight with no rows, the table fills its body with placeholder rows of
    // its own, which hold none of a template's fields.
    getRowId: (template, index) => template.id?.value ?? `skeleton-${index}`,
    muiSearchTextFieldProps: ({ table }) => ({
      ...searchTextFieldProps({ searches: PROPOSAL_TEMPLATES_SUBJECT, table }),
      sx: searchFieldWidth,
    }),
    muiTopToolbarProps: { sx: topToolbarFitsThePage },
    muiTableContainerProps: { sx: { flex: 1 } },
    muiTablePaperProps: { sx: { display: "flex", flexDirection: "column", height: "100%" } },
    renderEmptyRowsFallback: emptyRowsFallback({
      subject: PROPOSAL_TEMPLATES_SUBJECT,
      isLoading: templatesQuery.isFetching,
      isError: templatesQuery.isError,
      errorDescription: templatesQuery.isError ? getErrorMessageFromUnknown(templatesQuery.error) : undefined,
      onRetry: () => void templatesQuery.refetch(),
      description: NO_PROPOSAL_TEMPLATES_DESCRIPTION,
    }),
    renderRowActions: ({ row }) => {
      const template = row.original;
      const isDeleting = deletingTemplateId === template.id.value;
      return (
        <RowActions
          ariaLabel={`Actions for the proposal template ${template.name}`}
          actions={[
            {
              key: "start-draft",
              label: START_DRAFT_FROM_TEMPLATE_LABEL,
              ariaLabel: `Create a proposal from the template ${template.name}`,
              icon: <PostAddIcon />,
              disabled: !canCreateProposal || draftFromTemplate.isStarting,
              disabledReason: canCreateProposal
                ? "Starting the draft..."
                : "Creating a proposal requires read/write proposal access. Ask an administrator at your firm.",
              onClick: () => void draftFromTemplate.start(template.id),
            },
            {
              key: "delete",
              label: isDeleting ? "Deleting..." : DELETE_TEMPLATE_LABEL,
              ariaLabel: `Delete the proposal template ${template.name}`,
              icon: <DeleteIcon />,
              destructive: true,
              disabled: isDeleting,
              disabledReason: "Deleting...",
              onClick: () => void handleDelete(template),
            },
          ]}
        />
      );
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <ListPageHeading
        title="Proposals"
        tabs={tabs}
        mobileToolbar={
          isMediumDisplaySize ? (
            <ListPageMobileToolbar>
              <ListPageMobileSearch table={table} />
              {renderToolbarButtons(table)}
            </ListPageMobileToolbar>
          ) : undefined
        }
      />
    ),
    renderToolbarInternalActions: ({ table }) =>
      // An empty fragment, not null: the table draws its own default buttons in place of a null, and
      // the narrow layout then showed them twice.
      isMediumDisplaySize ? <></> : (
        <ListPageToolbarButtons>
          <ToolbarButtons>
            <GlobalFilterTextField table={table} sx={searchFieldSpacing} />
            <TableToolsMenu table={table} />
          </ToolbarButtons>
        </ListPageToolbarButtons>
      ),
    rowCount: templates.length,
    state: {
      isLoading: templatesQuery.isLoading,
      showProgressBars: templatesQuery.isFetching,
    },
  };

  return <MRTNoMemo<ProposalTemplate> options={options} />;
}

/**
 * The reader's templates that a draft could be started from in this session, newest first.
 *
 * Exported for the tab strip's count, so the badge and the table cannot disagree about how many
 * there are.
 */
export function usableTemplates(
  json: unknown,
  session: Parameters<ProposalTemplate["isUsableIn"]>[0],
): ProposalTemplate[] {
  return ProposalTemplate.fromJSONArray(json).filter((template) => template.isUsableIn(session));
}

function teamSize(template: ProposalTemplate): number {
  if (!template.team) return 0;
  return (template.team.leader ? 1 : 0) + template.team.members.length;
}

function describeTeamSize(size: number): ReactNode {
  if (size === 0) return <NoValueDash />;
  return size === 1 ? "1 person" : `${size} people`;
}

/**
 * Whether a template was created within the date-range filter's days, both ends included.
 *
 * The picker hands back moments at midnight, or nothing for an open end. The end day is taken as a
 * whole day, so a range ending today holds a template saved this afternoon.
 */
export function isCreatedWithin(created: Date, range: unknown): boolean {
  if (!Array.isArray(range)) return true;
  const [from, to] = range.map(toTimestamp);
  const time = created.getTime();
  if (from !== undefined && time < startOfDay(from)) return false;
  if (to !== undefined && time > endOfDay(to)) return false;
  return true;
}

/** The Created column's filter: the row's date against the range the date pickers hold. */
export function createdWithinFilter(
  row: Pick<MRT_Row<ProposalTemplate>, "getValue">,
  columnId: string,
  filterValue: unknown,
): boolean {
  return isCreatedWithin(row.getValue<Date>(columnId), filterValue);
}

function toTimestamp(value: unknown): number | undefined {
  if (value === null || value === undefined || value === "") return undefined;
  const time = typeof value === "object" && "valueOf" in value ? Number(value.valueOf()) : Number.NaN;
  return Number.isFinite(time) ? time : undefined;
}

function startOfDay(time: number): number {
  const day = new Date(time);
  day.setHours(0, 0, 0, 0);
  return day.getTime();
}

function endOfDay(time: number): number {
  const day = new Date(time);
  day.setHours(23, 59, 59, 999);
  return day.getTime();
}
